
import { Vue, Component, Watch } from 'vue-property-decorator';
import moment from 'moment';
import { BIconArrowRepeat } from 'bootstrap-vue';
import Api from '@gid/vue-common/api';
import DatePicker from '@gid/vue-common/components/DatePicker.vue';
import AgingChart from '../../components/accounting/AgingChart.vue';

interface AgingCohort {
  key: string;
  label: string;
}

interface AgingRow {
  accountSfid: string | null;
  accountName: string | null;
  accountType: string | null;
  notInvoicedRecent: number;
  notInvoicedStale: number;
  due30Plus: number;
  due0_30: number;
  d0_30: number;
  d31_60: number;
  d61_90: number;
  d90Plus: number;
  overdueTotal: number;
  openTotal: number;
  grandTotal: number;
}

const eur = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
});

@Component({
  components: { AgingChart, BIconArrowRepeat, DatePicker },
})
export default class AccountsAging extends Vue {
  kind: 'invoices' | 'costs' = 'invoices';
  role = '';
  topN: number | 'all' = 'all';
  issueDateFrom: string = moment('2025-01-01').startOf('day').toISOString(true);

  inProgress = false;
  errorMessage = '';

  allRows: AgingRow[] = [];
  cohorts: AgingCohort[] = [];
  selectedSfid = '';

  created() {
    this.fetch();
  }

  @Watch('issueDateFrom')
  onIssueDateChange() {
    this.fetch();
  }

  get kindOptions() {
    return [
      { value: 'invoices', text: this.$t('accounting.aging.invoices') },
      { value: 'costs', text: this.$t('accounting.aging.costs') },
    ];
  }

  get topNOptions() {
    return [
      { value: 10, text: 'Top 10' },
      { value: 20, text: 'Top 20' },
      { value: 50, text: 'Top 50' },
      { value: 'all', text: this.$t('accounting.aging.all') },
    ];
  }

  get roleOptions() {
    const roles = Array.from(
      new Set(this.allRows.map((r) => r.accountType).filter(Boolean)),
    ).sort();
    return [
      { value: '', text: this.$t('accounting.aging.all-roles') },
      ...roles.map((r) => ({ value: r, text: r })),
    ];
  }

  get filteredRows(): AgingRow[] {
    return this.role
      ? this.allRows.filter((r) => r.accountType === this.role)
      : this.allRows;
  }

  get displayRows(): AgingRow[] {
    return this.topN === 'all'
      ? this.filteredRows
      : this.filteredRows.slice(0, this.topN as number);
  }

  get totals() {
    const keys = [
      'notInvoicedRecent',
      'notInvoicedStale',
      'due30Plus',
      'due0_30',
      'd0_30',
      'd31_60',
      'd61_90',
      'd90Plus',
      'overdueTotal',
      'openTotal',
      'grandTotal',
    ] as const;
    const acc: Record<string, number> = {};
    for (const k of keys) {
      acc[k] = Number(
        this.filteredRows.reduce((s, r) => s + (r[k] || 0), 0).toFixed(2),
      );
    }
    return acc;
  }

  get chartHeight(): number {
    // ~28px per bar + headroom for legend/axes
    return Math.max(360, this.displayRows.length * 28 + 120);
  }

  // Cohort labels come from the backend in English; translate them here by key
  // so the chart legend and table headers follow the UI locale.
  get localizedCohorts(): AgingCohort[] {
    return this.cohorts.map((c) => ({
      key: c.key,
      label: this.$te(`accounting.aging.cohort.${c.key}`)
        ? (this.$t(`accounting.aging.cohort.${c.key}`) as string)
        : c.label,
    }));
  }

  get fields() {
    return [
      {
        key: 'accountName',
        label: this.$t('accounting.aging.account'),
        sortable: true,
      },
      ...this.localizedCohorts.map((c) => ({
        key: c.key,
        label: c.label,
        sortable: true,
        class: 'text-right',
      })),
      {
        key: 'overdueTotal',
        label: this.$t('accounting.aging.overdue-total'),
        sortable: true,
        class: 'text-right',
      },
      {
        key: 'openTotal',
        label: this.$t('accounting.aging.open-total'),
        sortable: true,
        class: 'text-right',
      },
      {
        key: 'grandTotal',
        label: this.$t('accounting.aging.grand-total'),
        sortable: true,
        class: 'text-right',
      },
    ];
  }

  async fetch() {
    this.inProgress = true;
    this.errorMessage = '';
    try {
      const { data } = await Api.get('/documents-api/accounting/aging', {
        params: {
          kind: this.kind,
          issueDateFrom: moment(this.issueDateFrom).format('YYYY-MM-DD'),
        },
        autoCancelScope: 'accounting-aging',
      });
      this.allRows = data.rows || [];
      this.cohorts = data.cohorts || [];
      // drop a role selection that no longer exists in the new dataset
      if (this.role && !this.allRows.some((r) => r.accountType === this.role)) {
        this.role = '';
      }
    } catch (e: any) {
      this.errorMessage = e?.response?.data?.message || e?.message || String(e);
      this.allRows = [];
    } finally {
      this.inProgress = false;
    }
  }

  onSelectAccount(row: AgingRow) {
    this.selectedSfid = row.accountSfid || '';
  }

  rowClass(item: AgingRow) {
    return item && item.accountSfid === this.selectedSfid
      ? 'table-primary'
      : '';
  }

  amountClass(value: number) {
    if (value < 0) return 'text-danger';
    return '';
  }

  formatEur(value: number): string {
    return eur.format(Number(value) || 0);
  }
}
